import React, { useState } from 'react';
import { X, Download, Edit, TrendingDown, Calendar, Building, Tag, FileText, ExternalLink, Printer } from 'lucide-react';
import { Button } from '../ui/Button';
import { formatCurrency, formatDate, downloadPDF } from '../../lib/utils';
import { PrintableExpense } from '../print/PrintableExpense';

interface ViewExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  expense: any;
}

export const ViewExpenseModal: React.FC<ViewExpenseModalProps> = ({
  isOpen,
  onClose,
  onEdit,
  expense,
}) => {
  const [showPrintView, setShowPrintView] = useState(false);
  const [downloading, setDownloading] = useState(false);

  if (!isOpen || !expense) return null;

  const getCategoryColor = (category: string) => {
    const colors = [
      'bg-blue-100 text-blue-700 border-blue-200',
      'bg-green-100 text-green-700 border-green-200',
      'bg-purple-100 text-purple-700 border-purple-200',
      'bg-orange-100 text-orange-700 border-orange-200',
      'bg-pink-100 text-pink-700 border-pink-200',
      'bg-indigo-100 text-indigo-700 border-indigo-200',
    ];
    const index = category.length % colors.length;
    return colors[index];
  };

  const handlePrint = () => {
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
      setShowPrintView(false);
    }, 100);
  };

  const handleDownloadExpense = async () => {
    setDownloading(true);
    try {
      // In a real app, this would generate a PDF using a library
      await downloadPDF(`Expense_${expense.id.slice(-8)}`, JSON.stringify(expense));
    } catch (error) {
      console.error('Error downloading expense report:', error);
      alert('Failed to download expense report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-error-50 to-error-100">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-error-100 rounded-lg flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-error-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Expense Details</h2>
                <p className="text-gray-600">Expense #{expense.id.slice(-8)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" icon={Edit} onClick={onEdit}>
                Edit
              </Button>
              <Button variant="ghost" size="sm" icon={Printer} onClick={handlePrint}>
                Print
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                icon={Download} 
                onClick={handleDownloadExpense}
                loading={downloading}
                disabled={downloading}
              >
                Export
              </Button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Expense Summary */}
            <div className="bg-gradient-to-r from-error-50 to-error-100 p-6 rounded-lg border border-error-200">
              <div className="text-center">
                <p className="text-error-600 text-sm font-medium mb-2">Expense Amount</p>
                <p className="text-4xl font-bold text-error-700">{formatCurrency(Number(expense.amount))}</p>
                <div className="mt-4">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getCategoryColor(expense.category)}`}>
                    <Tag className="w-4 h-4 mr-2" />
                    {expense.category}
                  </span>
                </div>
              </div>
            </div>

            {/* Expense Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Expense Information</h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-600">Expense ID:</span>
                    <p className="font-semibold text-gray-900">#{expense.id.slice(-8)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Date:</span>
                    <p className="text-gray-900 flex items-center">
                      <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                      {formatDate(expense.date)}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Recorded On:</span>
                    <p className="text-gray-900">{formatDate(expense.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Last Updated:</span>
                    <p className="text-gray-900">{formatDate(expense.updated_at)}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Vendor Information</h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-600">Vendor:</span>
                    <p className="font-semibold text-gray-900 flex items-center">
                      <Building className="w-4 h-4 mr-2 text-gray-500" />
                      {expense.vendor}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Category:</span>
                    <p className="text-gray-900">{expense.category}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Amount:</span>
                    <p className="font-bold text-lg text-error-600">{formatCurrency(Number(expense.amount))}</p>
                  </div>
                  {expense.receipt_url && (
                    <div>
                      <span className="text-sm text-gray-600">Receipt:</span>
                      <a
                        href={expense.receipt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 flex items-center"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Receipt
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Notes */}
            {expense.notes && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Notes
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700">{expense.notes}</p>
                </div>
              </div>
            )}

            {/* Expense Summary */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Expense Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Vendor:</span>
                  <span className="font-medium">{expense.vendor}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Category:</span>
                  <span className="font-medium">{expense.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span className="font-medium">{formatDate(expense.date)}</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between text-base">
                    <span className="font-semibold">Total Amount:</span>
                    <span className="font-bold text-lg text-error-600">{formatCurrency(Number(expense.amount))}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Receipt Link */}
            {expense.receipt_url && (
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h4 className="text-blue-800 font-semibold mb-2 flex items-center">
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Receipt Available
                </h4>
                <p className="text-blue-700 text-sm mb-3">
                  A receipt or supporting document is available for this expense.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={ExternalLink}
                  onClick={() => window.open(expense.receipt_url, '_blank')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  View Receipt
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Printable Version */}
      <PrintableExpense expense={expense} isVisible={showPrintView} />
    </>
  );
};